// ═══════════════════════════════════════════════════════════════════════════
//  AI Response Cards — Rich UI for AI-powered chat features
//  Time suggestions, meeting prep, quick replies, AI status badge
// ═══════════════════════════════════════════════════════════════════════════

import { useTheme } from "@/hooks/useTheme";
import { space, fontSize, fontWeight, radius, transition, semanticColor, lineHeight } from "@/styles/tokens";

// ─── Types ───────────────────────────────────────────────────────────────

interface TimeSuggestion {
  time: string;
  reason: string;
  score: number;
}

interface MeetingPrepData {
  type: "ai" | "template";
  content: string | null;
  meetingType: string;
  people: string[];
  topic: string;
  agenda?: string[];
  questions?: string[];
  timeAllocation?: string;
}

// ─── Time Suggestions Card ──────────────────────────────────────────────

export function TimeSuggestionsCard({
  suggestions,
  onSelect,
}: {
  suggestions: TimeSuggestion[];
  onSelect: (time: string) => void;
}) {
  const { colors: c } = useTheme();

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div
      style={{
        marginTop: space[2],
        borderRadius: radius["2xl"],
        border: `1px solid ${c.pri}30`,
        background: `${c.pri}08`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          background: `${c.pri}15`,
          fontSize: fontSize.sm,
          fontWeight: fontWeight.bold,
          color: c.pri,
          display: "flex",
          alignItems: "center",
          gap: space[1.5],
        }}
      >
        <span style={{ fontSize: fontSize.lg }}>&#x23F0;</span>
        Suggested Times
      </div>
      {suggestions.map((s, i) => {
        const date = new Date(s.time);
        const timeStr = date.toLocaleString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
        const scoreColor =
          s.score >= 8 ? "#10b981" : s.score >= 6 ? "#f59e0b" : "#6b7280";

        return (
          <button
            key={i}
            onClick={() => onSelect(s.time)}
            style={{
              width: "100%",
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "none",
              borderTop: i > 0 ? `1px solid ${c.bdr}` : "none",
              background: "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              transition: transition.bgFast,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${c.pri}12`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.txt }}>
                {timeStr}
              </div>
              <div style={{ fontSize: fontSize.xs, color: c.tx2, marginTop: space[0.5] }}>
                {s.reason}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 6,
                  borderRadius: radius.xs,
                  background: `${scoreColor}30`,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${s.score * 10}%`,
                    height: "100%",
                    borderRadius: radius.xs,
                    background: scoreColor,
                  }}
                />
              </div>
              <span style={{ fontSize: fontSize.xs, color: scoreColor, fontWeight: fontWeight.semibold }}>
                {s.score}/10
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Meeting Prep Card ──────────────────────────────────────────────────

export function MeetingPrepCard({ prep }: { prep: MeetingPrepData }) {
  const { colors: c } = useTheme();

  if (!prep) return null;

  // AI-generated markdown content
  if (prep.type === "ai" && prep.content) {
    return (
      <div
        style={{
          marginTop: space[2],
          borderRadius: radius["2xl"],
          border: `1px solid ${semanticColor.aiPurple}30`,
          background: `${semanticColor.aiPurple}08`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "8px 12px",
            background: `${semanticColor.aiPurple}15`,
            fontSize: fontSize.sm,
            fontWeight: fontWeight.bold,
            color: semanticColor.aiPurple,
            display: "flex",
            alignItems: "center",
            gap: space[1.5],
          }}
        >
          <span style={{ fontSize: fontSize.lg }}>&#x1F4CB;</span>
          Meeting Prep — {prep.meetingType}
        </div>
        <div
          style={{
            padding: "10px 12px",
            fontSize: fontSize.md,
            lineHeight: lineHeight.relaxed,
            color: c.txt,
            whiteSpace: "pre-wrap",
          }}
        >
          {prep.content}
        </div>
      </div>
    );
  }

  // Template-based prep
  return (
    <div
      style={{
        marginTop: space[2],
        borderRadius: radius["2xl"],
        border: `1px solid ${semanticColor.aiPurple}30`,
        background: `${semanticColor.aiPurple}08`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          background: `${semanticColor.aiPurple}15`,
          fontSize: fontSize.sm,
          fontWeight: fontWeight.bold,
          color: semanticColor.aiPurple,
          display: "flex",
          alignItems: "center",
          gap: space[1.5],
        }}
      >
        <span style={{ fontSize: fontSize.lg }}>&#x1F4CB;</span>
        Meeting Prep — {prep.meetingType}
        {prep.topic ? `: ${prep.topic}` : ""}
      </div>
      <div style={{ padding: "10px 12px" }}>
        {prep.agenda && prep.agenda.length > 0 && (
          <div style={{ marginBottom: space[2] }}>
            <div
              style={{
                fontSize: fontSize.sm,
                fontWeight: fontWeight.bold,
                color: c.pri,
                marginBottom: space[1],
              }}
            >
              Agenda
            </div>
            {prep.agenda.map((item, i) => (
              <div
                key={i}
                style={{
                  fontSize: fontSize.sm,
                  color: c.txt,
                  padding: "3px 0",
                  display: "flex",
                  gap: space[1.5],
                }}
              >
                <span style={{ color: c.tx2, minWidth: 14 }}>{i + 1}.</span>
                {item}
              </div>
            ))}
          </div>
        )}

        {prep.questions && prep.questions.length > 0 && (
          <div style={{ marginBottom: space[2] }}>
            <div
              style={{
                fontSize: fontSize.sm,
                fontWeight: fontWeight.bold,
                color: semanticColor.warning,
                marginBottom: space[1],
              }}
            >
              Questions to Ask
            </div>
            {prep.questions.map((q, i) => (
              <div
                key={i}
                style={{
                  fontSize: fontSize.sm,
                  color: c.txt,
                  padding: "3px 0",
                  display: "flex",
                  gap: space[1.5],
                }}
              >
                <span style={{ color: semanticColor.warning }}>?</span>
                {q}
              </div>
            ))}
          </div>
        )}

        {prep.timeAllocation && (
          <div
            style={{
              fontSize: fontSize.xs,
              color: c.tx2,
              padding: "6px 8px",
              background: `${c.pri}08`,
              borderRadius: radius.md,
              marginTop: space[1],
            }}
          >
            <strong>Time:</strong> {prep.timeAllocation}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Quick Reply Chips ──────────────────────────────────────────────────

export function QuickReplies({
  suggestions,
  onSelect,
}: {
  suggestions: string[];
  onSelect: (text: string) => void;
}) {
  const { colors: c } = useTheme();

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div
      style={{
        marginTop: space[1.5],
        display: "flex",
        gap: space[1],
        flexWrap: "wrap",
      }}
    >
      {suggestions.map((s, i) => (
        <button
          key={i}
          onClick={() => onSelect(s)}
          style={{
            padding: "5px 10px",
            borderRadius: radius["2xl"],
            border: `1px solid ${c.pri}40`,
            background: "transparent",
            color: c.pri,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: fontSize.sm,
            fontWeight: fontWeight.medium,
            transition: transition.fast,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = c.pri;
            e.currentTarget.style.color = semanticColor.white;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = c.pri;
          }}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ─── AI Status Badge ────────────────────────────────────────────────────

export function AIBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      style={{
        fontSize: fontSize["2xs"],
        padding: "2px 6px",
        borderRadius: radius.sm,
        background: enabled ? `${semanticColor.success}20` : `${semanticColor.neutral}20`,
        color: enabled ? semanticColor.success : semanticColor.neutral,
        fontWeight: fontWeight.bold,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
      }}
    >
      {enabled ? "AI" : "Local"}
    </span>
  );
}

// ─── Typing Indicator ───────────────────────────────────────────────────

export function TypingIndicator({ text }: { text?: string }) {
  const { colors: c } = useTheme();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: space[1.5],
        padding: "8px 0",
      }}
    >
      <div style={{ display: "flex", gap: 3 }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: c.pri,
              animation: `aiDotBounce 1.2s ease infinite ${i * 0.15}s`,
            }}
          />
        ))}
      </div>
      {text && (
        <span style={{ fontSize: fontSize.sm, color: c.tx2, fontStyle: "italic" }}>
          {text}
        </span>
      )}
    </div>
  );
}
